import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { setToken } from '../api/superAdminClient';

/**
 * 登入頁面
 * 使用 Google OAuth 登入，並支援 TOTP 驗證
 */
export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showTOTP, setShowTOTP] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  // 檢查 URL 參數（OAuth 回調）
  useEffect(() => {
    const token = searchParams.get('token');
    const tempTokenParam = searchParams.get('temp_token');
    const requiresTOTP = searchParams.get('requires_totp') === 'true';
    const emailParam = searchParams.get('email');

    if (token) {
      // 直接登入成功（不需要 TOTP）
      setToken(token);
      toast.success('登入成功');
      navigate('/dashboard');
      return;
    }

    if (tempTokenParam && requiresTOTP && emailParam) {
      // 需要 TOTP 驗證
      setTempToken(tempTokenParam);
      setEmail(emailParam);
      setShowTOTP(true);
      // 清除 URL 參數
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams, navigate]);

  /**
   * Google OAuth 登入
   */
  const handleGoogleLogin = () => {
    setIsLoading(true);
    const apiBase =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const redirectUri = `${window.location.origin}/login`;

    // 導向 Google OAuth
    window.location.href = `${apiBase}/api/super-admin/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  /**
   * 驗證 TOTP
   */
  const handleVerifyTOTP = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('請輸入 6 位數 TOTP 驗證碼');
      return;
    }

    if (!tempToken) {
      toast.error('缺少臨時 Token，請重新登入');
      setShowTOTP(false);
      return;
    }

    setIsLoading(true);
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
      const response = await fetch(
        `${apiBase}/api/super-admin/auth/verify-totp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tempToken,
            totpCode,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'TOTP 驗證失敗');
      }

      const result = await response.json();

      if (result.success && result.data?.token) {
        setToken(result.data.token);
        toast.success('登入成功');
        navigate('/dashboard');
      } else {
        throw new Error('TOTP 驗證失敗：未取得 Token');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'TOTP 驗證失敗');
      setTotpCode(''); // 清空輸入
    } finally {
      setIsLoading(false);
    }
  };

  // TOTP 驗證畫面
  if (showTOTP) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            Super Admin
          </h1>
          <p className="text-gray-600 mb-2 text-center">TOTP 驗證</p>
          {email && (
            <p className="text-sm text-gray-500 mb-6 text-center">{email}</p>
          )}

          <div className="space-y-6">
            <div>
              <label
                htmlFor="totp"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                TOTP 驗證碼
              </label>
              <input
                id="totp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totpCode}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTotpCode(value);
                  // 自動提交（當輸入 6 位數時）
                  if (value.length === 6) {
                    setTimeout(() => handleVerifyTOTP(), 100);
                  }
                }}
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="000000"
                autoFocus
                disabled={isLoading}
              />
              <p className="mt-2 text-sm text-gray-500 text-center">
                請從您的驗證器應用程式輸入 6 位數驗證碼
              </p>
            </div>

            <button
              onClick={handleVerifyTOTP}
              disabled={isLoading || totpCode.length !== 6}
              className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '驗證中...' : '驗證'}
            </button>

            <button
              onClick={() => {
                setShowTOTP(false);
                setTotpCode('');
                setTempToken(null);
                setEmail(null);
              }}
              className="w-full text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
            >
              返回
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Google OAuth 登入畫面
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
          Super Admin
        </h1>
        <p className="text-gray-600 mb-8 text-center">超管後台登入</p>

        <div className="space-y-4">
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>{isLoading ? '登入中...' : '使用 Google 登入'}</span>
          </button>

          <p className="text-xs text-gray-500 text-center">
            使用 Google 帳號登入超管後台
          </p>
        </div>
      </div>
    </div>
  );
}
